import type {
  AnalyticsEventType,
  AudienceType,
  BlogCategory,
  DurationBand,
  InquiryPriority,
  InquiryStatus,
  InquiryType,
  LearnerLevel,
  LearningMode,
  MediaVisibility,
  PostStatus,
  ProgramType,
  ResourceAccess,
  ResourceType,
  SkillType,
  UserRole,
} from './enums';

/** Shapes returned by the Vuganawe REST API (dates serialized as ISO strings). */

export interface ProgramDto {
  id: string;
  slug: string;
  title: string;
  type: ProgramType;
  summary: string;
  description: string;
  targetLearner: string;
  outcomes: string[];
  durationLabel: string;
  formatLabel: string;
  modes: LearningMode[];
  skillFocus: SkillType[];
  audiences: AudienceType[];
  featured: boolean;
  sortOrder: number;
  published: boolean;
  imageId: string | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ResourceDto {
  id: string;
  slug: string;
  title: string;
  summary: string;
  body: string | null;
  previewBody: string | null;
  accessLevel: ResourceAccess;
  tags: string[];
  featured: boolean;
  sortOrder: number;
  skill: SkillType;
  level: LearnerLevel;
  durationBand: DurationBand;
  durationMinutes: number;
  audience: AudienceType;
  type: ResourceType;
  imageId: string | null;
  imageUrl: string | null;
  /** Type-specific uploaded file (video / audio / worksheet PDF). */
  mediaId: string | null;
  mediaUrl: string | null;
  mediaMimeType: string | null;
  /** Optional lesson this resource supplements ("extra practice"). */
  lessonId: string | null;
  lessonSlug: string | null;
  lessonTitle: string | null;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LessonQuizQuestionDto {
  question: string;
  options: string[];
  answer: string;
  explanation?: string;
}

/** A lesson body is an ordered list of these blocks ("everything as blocks"). */
export type LessonBlockKind =
  | 'heading'
  | 'text'
  | 'callout'
  | 'activity'
  | 'image'
  | 'video'
  | 'youtube'
  | 'audio'
  | 'file'
  | 'vocabulary'
  | 'quiz'
  | 'gapfill'
  | 'matching'
  | 'speaking'
  | 'dictation'
  | 'table';

/** One left→right pair in a matching exercise. */
export interface MatchingPairDto {
  left: string;
  right: string;
}

/**
 * One structured vocabulary entry: the English term plus its meaning
 * (Kinyarwanda translation or a short definition) and an optional example
 * sentence. Powers real term→meaning flashcards and the lesson vocab list.
 */
export interface VocabEntryDto {
  term: string;
  meaning?: string;
  example?: string;
}

export interface LessonBlockDto {
  id: string;
  kind: LessonBlockKind;
  /** heading / text / callout / activity — and the prompt for speaking blocks */
  text?: string;
  /** callout style */
  tone?: 'note' | 'task';
  /** callout background colour (hex, e.g. "#fff4e5"); overrides the tone tint */
  bg?: string | null;
  /** media kinds (image/video/audio/file) — and the model audio for speaking */
  mediaId?: string | null;
  url?: string | null;
  mimeType?: string | null;
  /** optional caption shown under media / above vocab / quiz / exercises / activity */
  caption?: string;
  /** vocabulary (bare words, legacy) — gapfill sentences with answers in [brackets] — activity steps */
  items?: string[];
  /** vocabulary — structured term + meaning + example (preferred over items) */
  entries?: VocabEntryDto[];
  /** quiz */
  questions?: LessonQuizQuestionDto[];
  /** matching exercise */
  pairs?: MatchingPairDto[];
  /**
   * table — a rectangular grid of plain-text cells, row-major. Every row is
   * padded to the same length by the API, so renderers can index freely.
   */
  rows?: string[][];
  /** table — render the first row as a header (`<th>`). Defaults to true. */
  headerRow?: boolean;
}

export interface LearningModuleDto {
  id: string;
  slug: string;
  title: string;
  summary: string;
  /** "What you'll learn" bullets shown on the course page. */
  objectives: string[];
  skill: SkillType;
  level: LearnerLevel;
  sortOrder: number;
  featured: boolean;
  published: boolean;
  /** Optional course cover image (shown on cards + the course hero). */
  imageId: string | null;
  imageUrl: string | null;
  lessonCount: number;
  completedLessonCount: number;
  progressPercent: number;
  /** Sum of the published lessons' durations, in minutes. */
  totalDurationMinutes: number;
  /** How many lessons are free previews (accessLevel FREE). */
  freeLessonCount: number;
  /** Distinct learners who have started at least one lesson in the course. */
  learnerCount: number;
  /** Average 1–5 star rating, or null when unrated. */
  averageRating: number | null;
  ratingCount: number;
  /** The signed-in learner's own rating (null for guests / unrated). */
  myRating: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface LearningLessonDto {
  id: string;
  moduleId: string;
  moduleSlug: string;
  moduleTitle: string;
  moduleSkill: SkillType;
  moduleLevel: LearnerLevel;
  slug: string;
  title: string;
  introduction: string;
  writtenExplanation: string | null;
  vocabulary: string[];
  examples: string[];
  practiceActivity: string | null;
  quiz: LessonQuizQuestionDto[];
  homeworkTask: string | null;
  accessLevel: ResourceAccess;
  durationMinutes: number;
  sortOrder: number;
  featured: boolean;
  published: boolean;
  imageId: string | null;
  imageUrl: string | null;
  videoId: string | null;
  videoUrl: string | null;
  videoMimeType: string | null;
  audioId: string | null;
  audioUrl: string | null;
  audioMimeType: string | null;
  worksheetId: string | null;
  worksheetUrl: string | null;
  worksheetMimeType: string | null;
  progressPercent: number;
  completed: boolean;
  quizScore: number | null;
  favorite: boolean;
  feedback: LessonFeedbackDto[];
  /** Ordered lesson body — "everything as blocks". */
  content: LessonBlockDto[];
  /** Resources linked to this lesson as extra practice / downloads. */
  resources: ResourceDto[];
  nextLessonSlug: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LearningModuleDetailDto {
  module: LearningModuleDto;
  lessons: LearningLessonDto[];
}

export interface LessonFeedbackDto {
  id: string;
  lessonId: string;
  message: string;
  teacherName: string | null;
  createdAt: string;
}

/** Returned after rating a course — the fresh aggregate for the UI. */
export interface CourseRatingSummaryDto {
  averageRating: number | null;
  ratingCount: number;
  myRating: number;
}

/** One flashcard in the learner's vocabulary review deck. */
export interface VocabularyCardDto {
  word: string;
  /** Meaning/translation shown on the back of the flashcard (may be empty for legacy bare words). */
  meaning?: string;
  /** Optional example sentence shown under the meaning. */
  example?: string;
  lessonSlug: string;
  lessonTitle: string;
  moduleTitle: string;
}

/** SuperMemo-2 spaced-repetition state for one vocabulary word. */
export interface VocabReviewStateDto {
  /** Normalized word key. */
  word: string;
  reps: number;
  ef: number;
  interval: number;
  /** ISO date the card is next due. */
  dueAt: string;
  lastReviewedAt: string | null;
}

/** The signed-in learner's whole review deck state plus daily-goal totals. */
export interface VocabReviewSummaryDto {
  items: VocabReviewStateDto[];
  /** Cumulative vocabulary-practice XP. */
  xp: number;
  /** Distinct words reviewed today (drives the daily goal). */
  reviewsToday: number;
  /** Daily review-count goal. */
  dailyGoal: number;
}

/** The four spaced-repetition self-grades a learner can give a card. */
export type VocabGrade = 'again' | 'hard' | 'good' | 'easy';

/** The four learning games. */
export type GameId = 'word-match' | 'word-rush' | 'sentence-builder' | 'word-guess';

/** A learner's best result + play count for one game. */
export interface GameScoreDto {
  game: GameId;
  best: number;
  plays: number;
}

/** A word pair used by the vocabulary games (English ↔ Kinyarwanda). */
export interface GameWordPairDto {
  en: string;
  rw: string;
}

/** Admin-managed settings + content for one actual game (keyed by its id). */
export interface GameConfigDto {
  gameId: GameId;
  enabled: boolean;
  pairs: GameWordPairDto[];
  sentences: string[];
  updatedAt: string | null;
}

/** Per-game content the hub plays with (empty content ⇒ static fallback). */
export interface GameContentEntry {
  enabled: boolean;
  pairs: GameWordPairDto[];
  sentences: string[];
}

/** Content for every game, keyed by game id. */
export interface GameContentDto {
  games: Record<GameId, GameContentEntry>;
}

/** Result of submitting a game round: fresh best + XP awarded. */
export interface GameResultDto {
  game: GameId;
  best: number;
  plays: number;
  /** XP awarded for this round (server-computed and capped). */
  xpAwarded: number;
  /** The learner's new cumulative XP total (vocabulary + games). */
  totalXp: number;
}

/** Result of importing local guest progress after registration. */
export interface GuestProgressSyncDto {
  ok: true;
  alreadyImported: boolean;
  xpImported: number;
  vocabImported: number;
  gameRoundsImported: number;
}

export type CertificateScope = 'LESSON' | 'COURSE';

export interface CertificateListDto {
  id: string;
  certificateCode: string;
  certificateScope: CertificateScope;
  learnerName: string;
  moduleName: string;
  moduleSlug: string;
  lessonName: string | null;
  lessonSlug: string | null;
  completedAt: string;
  issuedAt: string;
  status: 'ISSUED' | 'REVOKED';
  averageScore: number | null;
}

/** Per-lesson engagement numbers for the admin insights view. */
export interface LessonInsightDto {
  lessonId: string;
  lessonSlug: string;
  lessonTitle: string;
  moduleTitle: string;
  published: boolean;
  /** Learners who opened the lesson at least once. */
  started: number;
  /** Learners who completed the lesson. */
  completed: number;
  averageQuizScore: number | null;
}

export interface LearningInsightsDto {
  /** Learners with at least one progress row. */
  activeLearners: number;
  lessonsStarted: number;
  lessonsCompleted: number;
  averageQuizScore: number | null;
  lessons: LessonInsightDto[];
}

/**
 * Product-health KPIs for the admin learning overview. Every number is
 * computed live from the database on each request — nothing cached.
 */
export interface LearningMetricsDto {
  /** Learners active today (UTC) / in the last 30 days. */
  dau: number;
  mau: number;
  /** DAU ÷ MAU as a percentage (null when MAU is 0). */
  stickiness: number | null;
  /** Learner-course pairs started / fully completed. */
  courseStarts: number;
  courseCompletions: number;
  /** Completions ÷ starts, % (null when nothing started). */
  courseCompletionRate: number | null;
  /** Learners who signed up ≥90 days ago (the measurable cohort). */
  a1Cohort: number;
  /** Of that cohort: completed a Beginner (≈CEFR A1) course within 90 days. */
  a1Reached: number;
  a1Within90DaysRate: number | null;
  /** Signed up ≥7d ago / came back within 7 days after signup. */
  retention7Cohort: number;
  retention7Rate: number | null;
  /** Signed up ≥30d ago / came back within 30 days after signup. */
  retention30Cohort: number;
  retention30Rate: number | null;
  /** Null until billing exists — shown honestly as "not tracked". */
  paidConversionRate: number | null;
  /** −100…100, from each learner's latest response (null = no responses). */
  npsScore: number | null;
  npsResponses: number;
  npsPromoters: number;
  npsPassives: number;
  npsDetractors: number;
}

/** One learner daily challenge (progress computed from today's activity). */
export interface DailyChallengeDto {
  /** Each id maps to a distinct activity: lesson, flashcards, games, or quiz. */
  id: 'lesson' | 'review' | 'games' | 'quiz';
  target: number;
  progress: number;
  completed: boolean;
}

export interface DailyChallengesDto {
  /** Kigali-local day the challenges refer to (YYYY-MM-DD). */
  day: string;
  /** Which round the learner is on today (1-based). */
  round: number;
  /** How many rounds are available per day (finishing a set unlocks the next). */
  maxRounds: number;
  challenges: DailyChallengeDto[];
  allCompleted: boolean;
  /** Bonus XP granted each time a round's challenges are all done. */
  bonusXp: number;
  /** True once the learner has claimed every round available today. */
  claimed: boolean;
}

/** One admin-tunable daily-challenge template (the pool the rotation draws from). */
export interface ChallengeTemplateDto {
  id: DailyChallengeDto['id'];
  /** Whether this template participates in the daily rotation. */
  enabled: boolean;
  /** Current target (admin-set, or the code default). */
  target: number;
  /** The code default target, shown so admins can compare/reset. */
  defaultTarget: number;
}

/** Admin view of the daily-challenge system: the template pool + today's set. */
export interface ChallengeAdminDto {
  /** How many challenges are shown to learners each day. */
  dailyCount: number;
  /** Bonus XP for finishing all of a day's challenges. */
  bonusXp: number;
  templates: ChallengeTemplateDto[];
  /** The set generated for today (preview of the rotation). */
  today: DailyChallengeDto[];
}

/** The shared word pool that lesson vocabulary feeds into the games. */
export interface GamesVocabPoolDto {
  /** Distinct {en → rw} pairs available from published lesson vocabulary. */
  wordCount: number;
  /** Minimum pairs before the pool feeds the games (below this, the static bank is used). */
  minRequired: number;
}

/** Whether/when the learner last answered the NPS survey. */
export interface NpsStatusDto {
  lastRespondedAt: string | null;
}

export interface LearnerLearningSummaryDto {
  totalLessons: number;
  completedLessons: number;
  progressPercent: number;
  averageQuizScore: number | null;
  /** Consecutive days (ending today or yesterday) with learning activity. */
  streakDays: number;
  continueLesson: LearningLessonDto | null;
  recentLessons: LearningLessonDto[];
  favoriteLessons: LearningLessonDto[];
  /* ── Gamification (server-tracked for signed-in learners) ─────────────── */
  /** All-time XP total (lessons + quizzes + reviews + games + courses). */
  xpTotal: number;
  /** Longest daily streak ever reached. */
  longestStreak: number;
  /** Activity for the current week, Sunday → Saturday (true = learned that day). */
  weekActivity: boolean[];
  /** Certificates earned (issued, not revoked). */
  certificatesEarned: number;
  /** Courses fully completed. */
  coursesCompleted: number;
  /** Cumulative vocabulary-practice XP. */
  reviewXp: number;
  /** Distinct vocabulary words reviewed today. */
  reviewsToday: number;
  /** Daily review-count goal. */
  dailyGoal: number;
  /** Words "learned" (reps ≥ 2). */
  learnedWords: number;
  /** Vocabulary words currently due for review. */
  dueWords: number;
}

export interface BlogPostDto {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: BlogCategory;
  status: PostStatus;
  publishedAt: string | null;
  authorName: string;
  readMinutes: number;
  imageId: string | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TestimonialDto {
  id: string;
  name: string;
  role: string;
  quote: string;
  programId: string | null;
  programTitle: string | null;
  approved: boolean;
  imageId: string | null;
  imageUrl: string | null;
  createdAt: string;
}

export interface InquiryDto {
  id: string;
  type: InquiryType;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  programInterest: string | null;
  audience: AudienceType | null;
  preferredMode: LearningMode | null;
  status: InquiryStatus;
  priority: InquiryPriority;
  internalNote: string | null;
  sourcePage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MediaAssetDto {
  id: string;
  bucket: string;
  objectKey: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  visibility: MediaVisibility;
  url: string | null;
  alt: string | null;
  createdAt: string;
}

export interface UserDto {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  /** Profile picture URL (set from Google sign-in), null otherwise. */
  avatarUrl: string | null;
  createdAt: string;
}

/* ── Leaderboard ─────────────────────────────────────────────────────────── */

export type LeaderboardPeriod = 'week' | 'all';

export interface LeaderboardEntryDto {
  rank: number;
  userId: string;
  name: string;
  avatarUrl: string | null;
  xp: number;
  /** True on the caller's own row (signed-in requests only). */
  isMe: boolean;
}

export interface LeaderboardDto {
  period: LeaderboardPeriod;
  items: LeaderboardEntryDto[];
  /** The caller's own row — present even when outside the top list. */
  me: LeaderboardEntryDto | null;
  /** Learners with any XP in this period. */
  totalLearners: number;
}

export interface AuthSessionDto {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
  user: UserDto;
}

/** Admin sessions share the generic auth session shape. */
export type AdminSessionDto = AuthSessionDto;

/**
 * Registration result. New accounts start unverified, so registration returns a
 * "check your email" state instead of a session; the session is issued once the
 * emailed link is confirmed.
 */
export interface RegisterResultDto {
  status: 'verification_required';
  email: string;
}

/**
 * Generic OK for actions that must not reveal whether an account exists
 * (resend verification, forgot password) — the response is identical either way.
 */
export interface AuthActionResultDto {
  ok: true;
}

export type NotificationType =
  | 'system'
  | 'reminder'
  | 'streak'
  | 'certificate'
  | 'challenge'
  | 'achievement';

export interface NotificationDto {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  href: string | null;
  read: boolean;
  createdAt: string;
}

export interface NotificationListDto {
  items: NotificationDto[];
  unreadCount: number;
}

export interface NotificationPreferencesDto {
  /** Whether "continue learning" reminder emails are sent. In-app reminders in
   *  the notification bell are always on regardless of this. */
  emailReminders: boolean;
}

export interface AnalyticsEventDto {
  id: string;
  eventType: AnalyticsEventType;
  page: string;
  label: string | null;
  sessionId: string | null;
  createdAt: string;
}

/* ── Aggregates for the admin dashboard ─────────────────────────────────── */

export interface DashboardOverview {
  totalInquiries: number;
  newInquiriesThisWeek: number;
  consultationBookings: number;
  placementTestRequests: number;
  corporateLeads: number;
  resourceInteractions: number;
  mostViewedPrograms: DistributionSlice[];
  /** converted inquiries / total inquiries, 0–1 */
  conversionRate: number;
}

export interface TrendPoint {
  /** ISO date (day granularity) */
  date: string;
  count: number;
}

export interface DistributionSlice {
  label: string;
  count: number;
}

export interface AnalyticsTrends {
  inquiriesOverTime: TrendPoint[];
  statusPipeline: DistributionSlice[];
  audienceDistribution: DistributionSlice[];
  ctaPerformance: DistributionSlice[];
}

export interface ResourcePopularity {
  topResources: DistributionSlice[];
  skillPopularity: DistributionSlice[];
}

/* ── Generic envelopes ──────────────────────────────────────────────────── */

export interface ListResponse<T> {
  items: T[];
  total: number;
}

export interface ApiError {
  statusCode: number;
  message: string | string[];
  error?: string;
}

/* ── Admin-managed UI translations ──────────────────────────────────────── */

/** One admin-editable UI string (dot-path key, e.g. "header.myLearning"). */
export interface UiTranslationDto {
  key: string;
  en: string;
  /** null/empty = missing Kinyarwanda (surfaced by the admin tracer filter). */
  rw: string | null;
  /** True once the admin edited the value — a code-seed re-sync won't touch it. */
  enCustom: boolean;
  rwCustom: boolean;
  updatedAt: string;
}

export interface UiTranslationListDto {
  items: UiTranslationDto[];
  total: number;
  /** Count of keys with no Kinyarwanda value (across the whole store). */
  missingRw: number;
}

/** Flat merged dictionaries served to the web app (DB over bundled JSON). */
export interface UiMessagesDto {
  /** Highest updatedAt in the store — usable as a cache-buster/etag. */
  version: string;
  en: Record<string, string>;
  rw: Record<string, string>;
}

export interface SyncUiTranslationsResultDto {
  created: number;
  /** Non-customized rows refreshed from the code seed. */
  updatedFromSeed: number;
  total: number;
  missingRw: number;
}

/* ── Course import (Markdown authoring format) ──────────────────────────── */

/**
 * One thing wrong with an imported course file. Errors block the import;
 * warnings are shown and allowed through (a media placeholder, say).
 */
export interface CourseImportIssueDto {
  level: 'error' | 'warning';
  /** Where it is, in author terms — "lesson «greetings» · block 4 (quiz)". */
  where: string;
  message: string;
}

/** What an import did, or would do when `applied` is false (a dry run). */
export interface CourseImportResultDto {
  /** False when the file could not be parsed or has blocking errors. */
  ok: boolean;
  /** True when the database was actually written to. */
  applied: boolean;
  /** Set when the file could not be parsed at all (nothing else is filled in). */
  parseError?: string;
  slug: string;
  title: string;
  lessonCount: number;
  blockCount: number;
  issues: CourseImportIssueDto[];
  /** Plan: what the import would change, resolved against what is stored. */
  willCreateCourse: boolean;
  willCreateLessons: string[];
  willUpdateLessons: string[];
  /** Lessons stored under this course that the file no longer lists. */
  orphanLessons: string[];
  /** Filled in only when `applied` is true. */
  lessonsCreated: number;
  lessonsUpdated: number;
  lessonsDeleted: number;
  /** Media attachments carried over from the previous version of a block. */
  mediaKept: number;
}
